import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { getEvaluationInstances, getUserEvaluationTasks, createEvaluationTask } from '@/services/evaluationService';

interface EvaluationInstance {
  id: string;
  name: string;
  description?: string;
  status: string;
}

interface EvaluationTask {
  id: string;
  instanceId: string;
  status: string;
  createdAt: string;
  completedAt?: string;
}

export function EvaluationPage() {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const [instances, setInstances] = useState<EvaluationInstance[]>([]);
  const [userTasks, setUserTasks] = useState<EvaluationTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingTask, setCreatingTask] = useState<string | null>(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [instancesRes, tasksRes] = await Promise.all([
        getEvaluationInstances(),
        getUserEvaluationTasks(),
      ]);
      
      setInstances(instancesRes.instances || []);
      setUserTasks(tasksRes.tasks || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };
  
  const handleStartEvaluation = async (instanceId: string) => {
    setCreatingTask(instanceId);
    setError('');
    
    try {
      const response = await createEvaluationTask(instanceId);
      const task = response.task;
      
      // 导航到评估任务页面
      navigate(`/evaluation/${instanceId}/task/${task.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建任务失败');
    } finally {
      setCreatingTask(null);
    }
  };
  
  const getInstanceStatus = (instanceId: string) => {
    const task = userTasks.find(t => t.instanceId === instanceId);
    if (!task) return null;
    
    if (task.status === 'completed') return 'completed';
    if (task.status === 'in_progress') return 'in_progress';
    return 'pending';
  };
  
  const getTaskId = (instanceId: string) => {
    const task = userTasks.find(t => t.instanceId === instanceId);
    return task?.id;
  };
  
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500">加载中...</div>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Automix 算法评估系统</h1>
        <p className="text-gray-600">
          欢迎，{user?.username}！请选择一个评估实例开始评估。
        </p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {instances.map(instance => {
          const status = getInstanceStatus(instance.id);
          const taskId = getTaskId(instance.id);
          const isCreating = creatingTask === instance.id;
          
          return (
            <div
              key={instance.id}
              className="bg-white rounded-lg border-2 border-gray-200 p-6 hover:border-sky-500 transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-bold text-lg">{instance.name}</h3>
                {status && (
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : status === 'in_progress'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {status === 'completed'
                      ? '已完成'
                      : status === 'in_progress'
                      ? '进行中'
                      : '待开始'}
                  </span>
                )}
              </div>
              
              {instance.description && (
                <p className="text-gray-600 text-sm mb-4">
                  {instance.description}
                </p>
              )}
              
              {status === 'completed' ? (
                <button
                  onClick={() => navigate(`/evaluation/${instance.id}/task/${taskId}`)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  查看结果
                </button>
              ) : status === 'in_progress' ? (
                <button
                  onClick={() => navigate(`/evaluation/${instance.id}/task/${taskId}`)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  继续评估
                </button>
              ) : (
                <button
                  onClick={() => handleStartEvaluation(instance.id)}
                  disabled={isCreating}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? '创建中...' : '开始评估'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      
      {instances.length === 0 && (
        <div className="text-center text-gray-500 py-12">
          暂无可用的评估实例
        </div>
      )}
    </div>
  );
}

